// Values from the design "One-page free tool" (desktop 1680). The design has no mobile frames;
// below 768px the layouts collapse to one column.

sn-free-tool {
  display: block;

  // `show-block="false"`: the display rule above would otherwise override the hidden attribute.
  &[hidden] {
    display: none;
  }

  .sn-free-tool {
    --sn-free-tool-surface: #e0ecfd; // TOKEN-GAP(signnow): was #e0ecfd, tool A page band
    --sn-free-tool-line: #d0dae4; // TOKEN-GAP(signnow): was #d0dae4, card border and dividers
    --sn-free-tool-field: #81919d; // TOKEN-GAP(signnow): was #81919d, input and canvas border
    --sn-free-tool-tile: #f6f8fb; // TOKEN-GAP(signnow): was #f6f8fb, font preview tile
    --sn-free-tool-tabbar: #ecf0f4; // TOKEN-GAP(signnow): was #ecf0f4

    padding: var(--spacing-3xl) var(--spacing-md) var(--spacing-5xl);
    background: var(--sn-free-tool-surface);
    color: var(--color-neutral-black);

    @include desktop {
      padding: var(--spacing-6xl) var(--spacing-2xl) px-to-rem(120px);
    }
  }

  .sn-free-tool__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-2xl);
    max-width: px-to-rem(1252px);
    margin: 0 auto;

    @include desktop {
      gap: var(--spacing-4xl);
    }
  }

  .sn-free-tool__intro {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    max-width: px-to-rem(896px);
    text-align: center;
  }

  .sn-free-tool__heading .me-heading {
    margin: 0;
    font-family: $sn-font-family-heading;
    font-size: px-to-rem(32px);
    font-weight: 700;
    line-height: px-to-rem(40px);
    color: var(--color-neutral-black);

    @include desktop {
      font-size: px-to-rem(52px);
      line-height: px-to-rem(64px);
    }
  }

  .sn-free-tool__description .me-text {
    margin: 0 auto;
    max-width: px-to-rem(816px);
    font-size: px-to-rem(16px);
    line-height: px-to-rem(24px);
    color: var(--color-neutral-black);

    @include desktop {
      font-size: px-to-rem(20px);
      line-height: px-to-rem(28px);
    }
  }

  // ------------------------------------------------------------------ tool A card

  .sn-free-tool__card {
    width: 100%;
    max-width: px-to-rem(776px);
    background: var(--color-neutral-white);
    border: 1px solid var(--sn-free-tool-line);
    border-radius: px-to-rem(12px);
    text-align: left;
  }

  .sn-free-tool__card-head {
    padding: var(--spacing-md) var(--spacing-md) var(--spacing-md);

    @include desktop {
      padding: var(--spacing-2xl) var(--spacing-2xl) var(--spacing-xl);
    }
  }

  .sn-free-tool__card-body {
    padding: 0 var(--spacing-md) var(--spacing-md);

    @include desktop {
      padding: 0 var(--spacing-2xl) var(--spacing-xl);
    }
  }

  .sn-free-tool__card-foot {
    display: flex;
    justify-content: flex-end;
    padding: var(--spacing-md);
    border-top: 1px solid var(--color-neutral-neutral-lighter);

    @include desktop {
      padding: var(--spacing-lg) var(--spacing-2xl);
    }

    me-button {
      width: 100%;

      @include above(600px) {
        width: auto;
      }
    }

    .me-button {
      width: 100%;
    }
  }

  .sn-free-tool__tabs {
    display: flex;
    padding: var(--spacing-2xs);
    background: var(--sn-free-tool-tabbar);
    border-radius: var(--radius-size-sm);
  }

  .sn-free-tool__tab {
    flex: 1 1 0;
    min-height: px-to-rem(32px);
    padding: var(--spacing-2xs) var(--spacing-sm);
    border: 0;
    border-radius: px-to-rem(2px);
    background: transparent;
    font-family: inherit;
    font-size: px-to-rem(14px);
    line-height: px-to-rem(20px);
    color: var(--color-neutral-neutral);
    cursor: pointer;

    &:hover {
      color: var(--color-neutral-black);
    }

    &:focus-visible {
      outline: 2px solid var(--color-brand-primary-main);
      outline-offset: -2px;
    }

    &--active {
      background: var(--color-neutral-white);
      box-shadow: 0 1px 2px rgb(0 0 0 / 5%);
      font-weight: 600;
      color: var(--color-neutral-black);
    }
  }

  .sn-free-tool__panel {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .sn-free-tool__label {
    margin: 0;
    font-size: px-to-rem(16px);
    font-weight: 600;
    line-height: px-to-rem(24px);
    color: var(--color-neutral-neutral-darkest);
  }

  .sn-free-tool__hint {
    margin: 0;
    font-size: px-to-rem(14px);
    line-height: px-to-rem(20px);
    color: var(--color-neutral-neutral-dark);

    &--center {
      text-align: center;
    }
  }

  .sn-free-tool__input {
    width: 100%;
    height: px-to-rem(32px);
    padding: 0 var(--spacing-xs);
    border: 1px solid var(--sn-free-tool-field);
    border-radius: var(--radius-size-sm);
    font-family: inherit;
    font-size: px-to-rem(14px);
    color: var(--color-neutral-black);

    &::placeholder {
      color: var(--color-neutral-neutral);
    }

    &:focus-visible {
      outline: none;
      border-color: var(--color-brand-primary-main);
      box-shadow: 0 0 0 1px var(--color-brand-primary-main);
    }
  }

  // Two columns at every width; on phones the previews shrink with the tiles instead of stacking
  // six full-size tiles into a screen and a half.
  .sn-free-tool__fonts {
    --sn-free-tool-font-scale: 0.5;

    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-xs);

    @include above(600px) {
      --sn-free-tool-font-scale: 1;

      gap: var(--spacing-sm);
    }
  }

  .sn-free-tool__font {
    display: flex;
    align-items: center;
    justify-content: center;
    height: px-to-rem(72px);
    padding: 0 var(--spacing-xs);

    @include above(600px) {
      height: px-to-rem(117px);
      padding: 0 var(--spacing-xl);
    }

    overflow: hidden;
    border: 0;
    border-radius: var(--radius-size-sm);
    background: var(--sn-free-tool-tile);
    color: #000; // TOKEN-GAP(signnow): was #000000, signature ink in previews
    line-height: 1.2;
    cursor: pointer;
    box-shadow: inset 0 0 0 1px transparent;
    transition: box-shadow 0.15s ease;

    &:hover {
      box-shadow: inset 0 0 0 1px var(--sn-free-tool-line);
    }

    &:focus-visible {
      outline: 2px solid var(--color-brand-primary-main);
      outline-offset: 2px;
    }

    &--selected,
    &--selected:hover {
      box-shadow: inset 0 0 0 2px var(--color-brand-primary-main);
    }
  }

  // Script fonts draw swashes past their advance width (Carattere's final stroke, Meddon's
  // loops); the inline padding keeps that ink inside the clip box that the ellipsis needs.
  .sn-free-tool__font-text {
    max-width: 100%;
    padding: 0.1em 0.4em;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  // ------------------------------------------------------------------ draw

  .sn-free-tool__draw-head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--spacing-xs);
  }

  .sn-free-tool__draw-actions,
  .sn-free-tool__editor-actions {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
  }

  // One row at 712px as in the design: 12px gaps, dividers without their own margin.
  .sn-free-tool__editor-actions {
    flex-wrap: wrap;
    gap: var(--spacing-sm);

    .sn-free-tool__divider {
      display: none;
      margin: 0;

      @include above(600px) {
        display: block;
      }
    }
  }

  .sn-free-tool__divider {
    width: 1px;
    height: px-to-rem(24px);
    margin: 0 var(--spacing-xs);
    background: var(--sn-free-tool-line);
  }

  .sn-free-tool__icon-button,
  .sn-free-tool__clear,
  .sn-free-tool__outline-button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-xs);
    font-family: inherit;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--color-brand-primary-main);
      outline-offset: 2px;
    }

    // Dim the content, not the button, so its tooltip stays readable.
    &:disabled {
      cursor: default;

      > * {
        opacity: 0.4;
      }
    }
  }

  .sn-free-tool__icon-button {
    width: px-to-rem(32px);
    height: px-to-rem(32px);
    padding: 0;
    border: 0;
    border-radius: var(--radius-size-sm);
    background: transparent;
    color: var(--color-neutral-neutral-dark);

    &:not(:disabled):hover {
      background: var(--color-neutral-neutral-lightest);
    }
  }

  .sn-free-tool__editor-actions > .sn-free-tool__icon-button {
    border: 1px solid var(--sn-free-tool-field);
    color: var(--color-neutral-neutral-darker);
  }

  .sn-free-tool__clear {
    height: px-to-rem(32px);
    padding: 0 var(--spacing-sm);
    border: 0;
    border-radius: var(--radius-size-sm);
    background: var(--color-neutral-neutral-lighter);
    font-size: px-to-rem(14px);
    font-weight: 600;
    color: var(--color-neutral-neutral-darker);

    &:not(:disabled):hover {
      background: var(--color-neutral-neutral-light);
    }
  }

  .sn-free-tool__canvas-wrap {
    position: relative;
    height: px-to-rem(240px);
    border: 1px solid var(--sn-free-tool-field);
    border-radius: px-to-rem(2px);

    @include desktop {
      height: px-to-rem(344px);
    }
  }

  .sn-free-tool__canvas {
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    height: 100%;
    touch-action: none;
    cursor: crosshair;
  }

  .sn-free-tool__baseline {
    position: absolute;
    left: 11%;
    right: 11%;
    bottom: 23%;
    height: 1px;
    background: #d5dade; // TOKEN-GAP(signnow): was #d5dade
  }

  // ------------------------------------------------------------------ upload (tool A)

  .sn-free-tool__dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-xs);
    min-height: px-to-rem(300px);
    padding: var(--spacing-xl) var(--spacing-md);
    border: 1px dashed #b9c2c8; // TOKEN-GAP(signnow): was #b9c2c8
    border-radius: var(--radius-size-sm);
    text-align: center;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    @include desktop {
      min-height: px-to-rem(396px);
    }

    &--active {
      border-color: var(--color-brand-primary-main);
      background: var(--color-brand-primary-lightest);
    }
  }

  .sn-free-tool__drop-title {
    margin: var(--spacing-xs) 0 0;
    font-size: px-to-rem(20px);
    font-weight: 600;
    line-height: px-to-rem(28px);
    color: var(--color-neutral-neutral-darkest);
  }

  .sn-free-tool__outline-button {
    height: px-to-rem(32px);
    margin-top: var(--spacing-xs);
    padding: 0 var(--spacing-sm);
    border: 1px solid var(--color-brand-primary-main);
    border-radius: var(--radius-size-sm);
    background: var(--color-neutral-white);
    font-size: px-to-rem(14px);
    font-weight: 600;
    color: var(--color-neutral-neutral-darker);

    &:hover {
      background: var(--color-brand-primary-lightest);
    }
  }

  .sn-free-tool__file {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .sn-free-tool__error {
    margin: var(--spacing-xs) 0 0;
    font-size: px-to-rem(14px);
    line-height: px-to-rem(20px);
    color: var(--color-system-error);
  }

  .sn-free-tool__slider {
    --fill: 50%;

    position: relative;
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-xs);
    color: var(--color-neutral-neutral);

    input[type='range'] {
      width: px-to-rem(160px);
      height: px-to-rem(20px);
      margin: 0;
      background: transparent;
      cursor: pointer;
      appearance: none;

      @include desktop {
        width: px-to-rem(205px);
      }

      &::-webkit-slider-runnable-track {
        height: 4px;
        border-radius: 16px;
        background: linear-gradient(90deg, var(--color-brand-primary-light) var(--fill), var(--color-neutral-neutral-lighter) var(--fill));
      }

      &::-moz-range-track {
        height: 4px;
        border-radius: 16px;
        background: linear-gradient(90deg, var(--color-brand-primary-light) var(--fill), var(--color-neutral-neutral-lighter) var(--fill));
      }

      &::-webkit-slider-thumb {
        width: 20px;
        height: 20px;
        margin-top: -8px;
        border: 1px solid var(--sn-free-tool-field);
        border-radius: 50%;
        background: var(--color-neutral-white);
        appearance: none;
      }

      &::-moz-range-thumb {
        width: 20px;
        height: 20px;
        border: 1px solid var(--sn-free-tool-field);
        border-radius: 50%;
        background: var(--color-neutral-white);
      }

      &:focus-visible {
        outline: 2px solid var(--color-brand-primary-main);
        outline-offset: 2px;
        border-radius: 4px;
      }
    }

    // Rotation has no "amount" to fill: a neutral track with a tick marking 0° in the middle.
    &--centered input[type='range'] {
      &::-webkit-slider-runnable-track {
        background: var(--color-neutral-neutral-lighter);
      }

      &::-moz-range-track {
        background: var(--color-neutral-neutral-lighter);
      }
    }
  }

  .sn-free-tool__range {
    position: relative;
    display: inline-flex;

    input[type='range'] {
      position: relative;
      z-index: 1;
    }
  }

  .sn-free-tool__range-tick {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 2px;
    height: 8px;
    border-radius: 2px;
    background: #b9c2c8; // TOKEN-GAP(signnow): was #b9c2c8
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  .sn-free-tool__select {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-xs);
    width: px-to-rem(148px);
    height: px-to-rem(32px);
    padding: 0 var(--spacing-xs);
    border: 1px solid #738592; // TOKEN-GAP(signnow): was #738592
    border-radius: var(--radius-size-sm);
    background: var(--color-neutral-white);
    color: var(--color-neutral-neutral);

    select {
      position: absolute;
      inset: 0;
      padding-left: px-to-rem(32px);
      border: 0;
      background: transparent;
      font-family: inherit;
      font-size: px-to-rem(14px);
      font-weight: 600;
      color: var(--color-neutral-neutral-darker);
      cursor: pointer;
      appearance: none;

      &:focus-visible {
        outline: 2px solid var(--color-brand-primary-main);
        outline-offset: 2px;
        border-radius: var(--radius-size-sm);
      }
    }

    svg:last-child {
      margin-left: auto;
      pointer-events: none;
    }
  }

  .sn-free-tool__preview {
    position: relative;
    height: px-to-rem(260px);
    overflow: hidden;
    border: 1px solid #d5dade; // TOKEN-GAP(signnow): was #d5dade
    border-radius: var(--radius-size-sm);
    background-color: var(--color-neutral-white);
    background-image:
      linear-gradient(45deg, var(--color-neutral-neutral-lightest) 25%, transparent 25%, transparent 75%, var(--color-neutral-neutral-lightest) 75%),
      linear-gradient(45deg, var(--color-neutral-neutral-lightest) 25%, transparent 25%, transparent 75%, var(--color-neutral-neutral-lightest) 75%);
    background-position: 0 0, 24px 24px;
    background-size: 48px 48px;

    @include desktop {
      height: px-to-rem(344px);
    }
  }

  // Crop mode: the whole preview is the selection surface, the box sits on it.
  .sn-free-tool__crop-area {
    position: absolute;
    z-index: 2;
    inset: 0;
    cursor: crosshair;
    touch-action: none;
  }

  .sn-free-tool__crop-box {
    position: absolute;
    border: 1px dashed var(--color-neutral-neutral-dark);
    cursor: move;

    &:focus-visible {
      outline: 2px solid var(--color-brand-primary-main);
      outline-offset: 2px;
    }
  }

  .sn-free-tool__crop-handle {
    position: absolute;
    width: 10px;
    height: 10px;
    border: 1px solid var(--color-neutral-neutral-dark);
    border-radius: 2px;
    background: var(--color-neutral-white);
    transform: translate(-50%, -50%);

    // A larger invisible hit area than the visible square, for fingers.
    &::after {
      content: '';
      position: absolute;
      inset: -8px;
    }

    &--nw {
      top: 0;
      left: 0;
      cursor: nwse-resize;
    }

    &--n {
      top: 0;
      left: 50%;
      cursor: ns-resize;
    }

    &--ne {
      top: 0;
      left: 100%;
      cursor: nesw-resize;
    }

    &--e {
      top: 50%;
      left: 100%;
      cursor: ew-resize;
    }

    &--se {
      top: 100%;
      left: 100%;
      cursor: nwse-resize;
    }

    &--s {
      top: 100%;
      left: 50%;
      cursor: ns-resize;
    }

    &--sw {
      top: 100%;
      left: 0;
      cursor: nesw-resize;
    }

    &--w {
      top: 50%;
      left: 0;
      cursor: ew-resize;
    }
  }

  .sn-free-tool__zoom {
    position: absolute;
    z-index: 3;
    bottom: var(--spacing-xs);
    left: 50%;
    display: flex;
    align-items: center;
    gap: var(--spacing-2xs);
    transform: translateX(-50%);

    input[type='range'] {
      width: px-to-rem(160px);
      accent-color: var(--color-brand-primary-light);
    }
  }

  // Tooltips on hover and keyboard focus; `data-tooltip` carries the text, aria-label the name.
  [data-tooltip] {
    position: relative;

    &::after {
      content: attr(data-tooltip);
      position: absolute;
      z-index: 5;
      bottom: calc(100% + 8px);
      left: 50%;
      padding: var(--spacing-2xs) var(--spacing-xs);
      border-radius: var(--radius-size-sm);
      background: var(--color-neutral-neutral-darkest);
      font-size: px-to-rem(12px);
      font-weight: 400;
      line-height: px-to-rem(16px);
      color: var(--color-neutral-white);
      white-space: nowrap;
      pointer-events: none;
      opacity: 0;
      transform: translateX(-50%);
      transition: opacity 0.15s ease;
    }

    // The pointer under the bubble, as in the design.
    &::before {
      content: '';
      position: absolute;
      z-index: 5;
      bottom: calc(100% + 3px);
      left: 50%;
      border: 5px solid transparent;
      border-bottom-width: 0;
      border-top-color: var(--color-neutral-neutral-darkest);
      pointer-events: none;
      opacity: 0;
      transform: translateX(-50%);
      transition: opacity 0.15s ease;
    }

    &:hover::after,
    &:hover::before,
    &:focus-within::after,
    &:focus-within::before,
    &:focus-visible::after,
    &:focus-visible::before {
      opacity: 1;
    }
  }

  // ------------------------------------------------------------------ tool B

  .sn-free-tool--sign {
    background: var(--color-neutral-white);

    .sn-free-tool__container {
      flex-direction: column;
      align-items: stretch;
      gap: var(--spacing-xl);
      padding: var(--spacing-xl) var(--spacing-md);
      border-radius: px-to-rem(12px);
      background: #e9f1ff; // TOKEN-GAP(signnow): was #e9f1ff

      @include desktop {
        flex-direction: row;
        align-items: center;
        gap: var(--spacing-2xl);
        padding: var(--spacing-4xl);
      }
    }

    .sn-free-tool__intro {
      gap: var(--spacing-xs);
      text-align: left;

      @include desktop {
        flex: 0 1 px-to-rem(455px);
      }
    }

    .sn-free-tool__heading .me-heading {
      font-size: px-to-rem(32px);
      line-height: px-to-rem(40px);

      @include desktop {
        font-size: px-to-rem(40px);
        line-height: px-to-rem(48px);
      }
    }

    .sn-free-tool__description .me-text {
      margin: 0;
      font-size: px-to-rem(16px);
      line-height: px-to-rem(24px);
    }
  }

  .sn-free-tool__features {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    margin-top: var(--spacing-xl);

    .me-text {
      position: relative;
      margin: 0;
      padding-left: px-to-rem(36px);
      font-size: px-to-rem(16px);
      line-height: px-to-rem(24px);

      &::before {
        content: '';
        position: absolute;
        top: 4px;
        left: 4px;
        width: 16px;
        height: 16px;
        background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='%230777CF' d='M12.27 4.32a1 1 0 0 1 1.46 1.36l-6.5 7a1 1 0 0 1-1.44.03l-3.5-3.5a1 1 0 0 1 1.42-1.42l2.76 2.77z'/%3E%3C/svg%3E") center / contain no-repeat;
      }
    }
  }

  .sn-free-tool__upload-card {
    position: relative;
    flex: 1 1 auto;
    padding: var(--spacing-sm);
    border-radius: px-to-rem(12px);
    background: var(--color-neutral-white);
    box-shadow: 0 2px 8px rgb(44 44 44 / 23%);
    text-align: center;

    &--limit {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-xs);
      padding: var(--spacing-4xl) var(--spacing-xl);
    }
  }

  .sn-free-tool__upload-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-md);
    padding: var(--spacing-2xl) var(--spacing-md);
    border: 1px dashed #b2c2d2; // TOKEN-GAP(signnow): was #b2c2d2
    border-radius: var(--radius-size-md);
    transition: background-color 0.15s ease, border-color 0.15s ease;

    @include desktop {
      padding: px-to-rem(34px) var(--spacing-4xl);
    }

    .me-button {
      min-width: px-to-rem(229px);
    }
  }

  .sn-free-tool__upload-card--active .sn-free-tool__upload-inner {
    border-color: var(--color-brand-primary-main);
    background: var(--color-brand-primary-lightest);
  }

  .sn-free-tool__drop-text {
    margin: 0;
    font-size: px-to-rem(18px);
    line-height: px-to-rem(28px);

    @include desktop {
      font-size: px-to-rem(20px);
    }
  }

  .sn-free-tool__or {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    width: 100%;
    max-width: px-to-rem(150px);
    margin: 0;
    font-size: px-to-rem(16px);
    line-height: px-to-rem(24px);

    &::before,
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: var(--color-neutral-neutral-light);
    }
  }

  .sn-free-tool__limit-title {
    margin: var(--spacing-xs) 0 0;
    font-family: $sn-font-family-heading;
    font-size: px-to-rem(24px);
    font-weight: 700;
    line-height: px-to-rem(32px);
  }

  .sn-free-tool__limit-text {
    margin: 0;
    font-size: px-to-rem(16px);
    line-height: px-to-rem(24px);
  }

  .sn-free-tool__limit-actions {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
    width: 100%;
    max-width: px-to-rem(573px);
    margin-top: var(--spacing-md);

    @include above(600px) {
      flex-direction: row;
    }

    me-button {
      flex: 1 1 0;
    }

    .me-button {
      width: 100%;
    }
  }
}
